/**
 * 用語・ラベルの単一辞書（UI 表示用）。
 *
 * 設計意図 (issue #36, #43):
 *   - リテラシー低めの動画販売業者向けに、enum / 業界用語を「一読で意味が通じる」表現に統一する
 *   - 「DL」「UL」「サインイン」「フック領域」「凍結」「ステルス」「メアド」等は画面から完全に排除する
 *   - enum 値ごとに 1 ラベルを `satisfies Record<…, string>` で型ガード
 *     → enum に値を追加すると未定義語があればコンパイルエラーで気付ける
 *   - 文章レベルのコピー（モーダル本文や説明文）は各画面に残してよい。
 *     辞書には「短いラベル」だけを集約する。
 *
 * 関連:
 *   - docs/ui-copy-guide.md（判断基準・置き換え表）
 *   - packages/shared/src/copy/user-messages.ts（API/メールと共有する文章）
 *   - BRD §6.D (プラン・暫定テナント)
 *   - SRD §4.1 (ロール)
 *   - packages/db/prisma/schema.prisma の各 enum
 */

import type { AccessAction, ActorType, SharePermission } from '@terafile/shared'

// ---------------------------------------------------------------------------
// 共有URL 権限（SharePermission）
// ---------------------------------------------------------------------------

export const SHARE_PERMISSION_LABEL = {
  VIEW: 'ブラウザで再生のみ',
  DOWNLOAD: '再生 + ダウンロード',
  UPLOAD: 'ファイルを受け取る',
} as const satisfies Record<SharePermission, string>

/**
 * 共有URL 作成ダイアログ向けの 1〜2 行の補足説明。
 * モーダルやセレクト UI でラベルだけだと意味が分かりにくい場合に併記する。
 */
export const SHARE_PERMISSION_DESCRIPTION = {
  VIEW: '相手はブラウザで再生できます。ダウンロードはできません。',
  DOWNLOAD: '相手はブラウザでの再生に加えて、元ファイルを保存できます。',
  UPLOAD: '相手からファイルを送ってもらう窓口になります。',
} as const satisfies Record<SharePermission, string>

// ---------------------------------------------------------------------------
// アクセスログのアクション種別（AccessAction）
// ---------------------------------------------------------------------------

export const ACCESS_ACTION_LABEL = {
  VIEW: '閲覧',
  PLAY_START: '再生開始',
  PLAY_PROGRESS: '再生中',
  PLAY_END: '再生終了',
  DOWNLOAD: 'ダウンロード',
  UPLOAD: 'アップロード',
  ARCHIVE: 'ゴミ箱に移す',
  UNARCHIVE: '元に戻す',
  DELETE: '完全に消す',
} as const satisfies Record<AccessAction, string>

// ---------------------------------------------------------------------------
// アクセスログの実行者種別（ActorType）
// ---------------------------------------------------------------------------

export const ACTOR_TYPE_LABEL = {
  OWNER: '自分',
  GUEST: '共有相手',
  ADMIN: '運営',
} as const satisfies Record<ActorType, string>

// ---------------------------------------------------------------------------
// プラン区分（Plan）
// ---------------------------------------------------------------------------

// @terafile/shared の PlanSchema は ['FREE', 'PRO', 'EPHEMERAL'] だが、
// アプリ側で重複型定義を避けるためにキーをリテラルで宣言する。
type PlanKey = 'FREE' | 'PRO' | 'EPHEMERAL'

export const PLAN_LABEL = {
  FREE: '無料プラン',
  PRO: '有料プラン',
  EPHEMERAL: 'お試し',
} as const satisfies Record<PlanKey, string>

export function getPlanLabel(plan: PlanKey): string {
  return PLAN_LABEL[plan]
}

// ---------------------------------------------------------------------------
// ゴミ箱操作（UI ボタン・バッジ・toast 用）
// 内部 API / DB は archived_at を維持。画面だけ Mac 風ゴミ箱語彙（issue #86）。
// ---------------------------------------------------------------------------

/** pg_cron の自動完全削除までの日数（F-ARC-04） */
export const TRASH_RETENTION_DAYS = 30

export const TRASH_ACTION = {
  tab: 'ゴミ箱',
  activeTab: 'アクティブ',
  delete: '削除',
  restore: '元に戻す',
  purgeNow: '今すぐ完全に消す',
  trashedBadge: 'ゴミ箱',
  toastMovedFile: 'ゴミ箱に移しました（30日後に自動で消えます）',
  toastRestoredFile: '元に戻しました',
  toastPurgedFile: '完全に消しました',
  toastMovedProject: 'ゴミ箱に移しました（30日後に自動で消えます）',
  toastRestoredProject: '元に戻しました',
  toastPurgedProject: '完全に消しました',
} as const

/** archivedAt から自動完全削除までの残日数（切り上げ） */
export function getTrashRemainingDays(archivedAt: string, now = Date.now()): number {
  const purgeAt = new Date(archivedAt).getTime() + TRASH_RETENTION_DAYS * 24 * 60 * 60 * 1000
  return Math.max(0, Math.ceil((purgeAt - now) / (24 * 60 * 60 * 1000)))
}

// ---------------------------------------------------------------------------
// 共有URL 状態・操作（UI ボタン・バッジ用）
// ---------------------------------------------------------------------------

export const SHARE_LINK_STATUS = {
  expired: '期限切れ',
  revoked: '使えなくなった',
} as const

export const SHARE_LINK_ACTION = {
  revoke: '使えなくする',
  revokeConfirm: 'この共有URLを使えなくしますか？',
  revokeBeforeCreate: '既に共有URLがあります。新しく作る前に、既存のURLを使えなくしてください。',
} as const

// ---------------------------------------------------------------------------
// テナント種別（TenantKind）
// ---------------------------------------------------------------------------

type TenantKindKey = 'REGULAR' | 'EPHEMERAL'

export const TENANT_KIND_LABEL = {
  REGULAR: '通常',
  EPHEMERAL: 'お試し',
} as const satisfies Record<TenantKindKey, string>

// ---------------------------------------------------------------------------
// テナント状態（TenantStatus）
// ---------------------------------------------------------------------------

type TenantStatusKey = 'ACTIVE' | 'SUSPENDED' | 'FROZEN'

export const TENANT_STATUS_LABEL = {
  ACTIVE: '利用中',
  SUSPENDED: '一時停止中',
  FROZEN: '60日未ログインで停止',
} as const satisfies Record<TenantStatusKey, string>

// ---------------------------------------------------------------------------
// 動画のトランスコード状態（StreamStatus）
// ---------------------------------------------------------------------------

type StreamStatusKey = 'PENDING' | 'QUEUED' | 'READY' | 'ERROR'

/**
 * 動画ステータスのラベル。
 * - PENDING / QUEUED は中身が違うが、ユーザー視点では同じ「準備中」なので同一文言にする
 * - 末尾の三点リーダ「…」は処理中の暗黙的サイン
 */
export const STREAM_STATUS_LABEL = {
  PENDING: '動画を準備しています…通常 2〜3 分かかります',
  QUEUED: '動画を準備しています…通常 2〜3 分かかります',
  READY: '再生できます',
  ERROR: '動画の準備に失敗しました',
} as const satisfies Record<StreamStatusKey, string>

// ---------------------------------------------------------------------------
// 共通ローディング文言
// ---------------------------------------------------------------------------

/**
 * 全画面で揺れがちな「読み込み中…」「進行中…」「準備中…」を一箇所に集約。
 * 同義のものは同一文言に揃えること。
 */
export const LOADING = {
  /** GET 系の一覧・詳細データ取得中 */
  fetching: '読み込み中…',
  /** PUT/PATCH/POST で書き込み中（編集保存・設定変更） */
  saving: '保存中…',
  /** メール送信・申請送信など、ユーザーが「送る」操作をした時 */
  sending: '送信中…',
} as const
